'use client';

/**
 * useTripPlanner — the trip being planned and the handlers that edit it.
 *
 * Moved out of HomeClient.tsx (behaviour unchanged) so planner work edits
 * planner files. HomeClient still owns everything that is about the explorer
 * page rather than the trip: map view mode, route search, saved-trip sync.
 *
 *   useTripPlanner  stops, active trip id, status line, waypoint mode +
 *                   add / remove / reorder / rename / note / nights handlers
 *   useTripLegs     the legs + totals shown everywhere (one source of truth)
 *   useNightPlan    day split + night slots (signed in only)
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { LatLng } from '../../utils/routingApi';
import { betterSpot, insertStopAt, isLoopTrip, makeStopTheDestination, mergeTripStopIntoList, moveStopToGap } from '../../utils/tripInsert';
import { compassWord, nearestLand, reverseGeocodePlace, type LandPoint } from '../../utils/geocodeApi';
import { snapPinToRoute } from '../../utils/routeSnap';
import { fetchAllMapPoints } from '../../lib/searchEntities';
import { mapToastLifetime, type MapToastState } from './MapToast';
import { revealInScroller, whereItWent } from './reveal';
import { normaliseTripStops, setStopNights } from '../../utils/tripRoles';
import { paceLegs, pickTripLegs, pointAlongPolyline, rememberRoadLegs, totalsFromLegs, type LegSource, type RoadLegCache } from '../../utils/routeLegs';
import { planNights, withSlotNames } from '../../utils/nightPlanner';
import type { LadderLeg, TripStatusMessage, TripStop } from './types';

export const uniqueId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `trip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createTripStopFromName = (
  name: string,
  kind: TripStop['kind'],
  options: Partial<TripStop> = {}
): TripStop => ({
  id: options.id ?? uniqueId(),
  name,
  kind,
  locked: options.locked ?? false,
  entityId: options.entityId,
  lat: options.lat,
  lng: options.lng,
  nights: options.nights,
  tags: options.tags ?? [],
  constraints: options.constraints ?? [],
  notes: options.notes,
});

/** Google returns "Plus Codes" (e.g. "5QC8PHMF+C7") for points with no nearby place. */
export const isPlusCode = (s: string) => /(^|\s)[23456789CFGHJMPQRVWX]{4,}\+[23456789CFGHJMPQRVWX]{2,}/i.test(s);

/** Remove one stop and return an Undo that puts it back where it was. */
export function removeStopWithUndo(
  stops: TripStop[],
  stopId: string
): { next: TripStop[]; removed: TripStop; restore: (cur: TripStop[]) => TripStop[] } | null {
  const index = stops.findIndex((stop) => stop.id === stopId);
  if (index === -1) return null;
  const removed = stops[index];
  return {
    next: stops.filter((stop) => stop.id !== stopId),
    removed,
    restore: (cur) => {
      if (cur.some((s) => s.id === removed.id)) return cur;
      const next = [...cur];
      next.splice(Math.min(index, next.length), 0, removed);
      return next;
    },
  };
}

export interface VehicleRouteState {
  key: string;
  polyline: LatLng[];
  legs: { distanceKm: number; driveMinutes: number }[];
  vertexIndex: number[];
  cumulativeKm: number[];
}

export interface GoogleLegsState {
  key: string;
  legs: { distanceKm: number; driveMinutes: number; via?: string }[];
  /** The road Google drew for each leg (leg camp searches follow it). */
  paths?: LatLng[][];
}

/** A place picked in a search box (LocationAutocomplete's ResolvedLocation). */
export interface PickedPlace {
  label: string;
  lat: number;
  lng: number;
  entityId?: number;
}

/** A stop from a typed place: a campsite is somewhere you sleep (1 night);
 *  a town is a pass-through until you add a night on its row. */
function stopFromPlace(place: PickedPlace): TripStop {
  const isCamp = typeof place.entityId === 'number';
  return createTripStopFromName(place.label, isCamp ? 'camp' : 'via', {
    lat: place.lat,
    lng: place.lng,
    entityId: place.entityId,
    nights: isCamp ? 1 : 0,
  });
}

export function useTripPlanner({
  setMapViewMode,
  routePathRef,
  mapRef,
}: {
  setMapViewMode: (mode: 'search' | 'trip') => void;
  /** The road line the trip is drawn with right now: a pin dropped beside it
   *  snaps onto it. */
  routePathRef?: MutableRefObject<{ lat: number; lng: number }[] | null>;
  /** The live map, for its zoom (how far "beside the route" reaches). */
  mapRef?: MutableRefObject<google.maps.Map | null>;
}) {
  const [tripStops, setTripStops] = useState<TripStop[]>([]);
  const [activeTripId, setActiveTripId] = useState<string | null>(null);
  const [tripStatusMessage, setTripStatusMessage] = useState<TripStatusMessage | null>(null);
  // A message with a ttl (an Undo toast) clears itself, unless a newer one
  // has replaced it.
  useEffect(() => {
    if (!tripStatusMessage?.ttlMs) return;
    const msg = tripStatusMessage;
    const t = setTimeout(() => setTripStatusMessage((cur) => (cur === msg ? null : cur)), msg.ttlMs);
    return () => clearTimeout(t);
  }, [tripStatusMessage]);
  // ── Map waypoint editing (GaiaGPS-style) ─────────────────────────────────
  // Toggle a mode where clicking the map drops a waypoint into the trip. The
  // route between stops is drawn with Google Directions, so it snaps to the
  // road/highway network automatically as waypoints are added.
  const [addWaypointMode, setAddWaypointMode] = useState(false);
  const tripStopsRef = useRef(tripStops);
  tripStopsRef.current = tripStops;

  const reverseGeocodeName = useCallback(async (lat: number, lng: number): Promise<string> => {
    const fallback = `Custom stop (${lat.toFixed(3)}, ${lng.toFixed(3)})`;
    // Plus Codes are meaningless as a stop name — reject them and fall back
    // to the nearest town, then region ("Stop near …"), then a clean address.
    try {
      if (typeof google === 'undefined' || !google.maps?.Geocoder) return fallback;
      const geocoder = new google.maps.Geocoder();
      const res = await geocoder.geocode({ location: { lat, lng } });
      const results = (res.results || []).filter((r) => !r.types?.includes('plus_code'));
      if (!results.length) return fallback;

      const pick = (types: string[]): string | undefined => {
        for (const r of results) {
          for (const c of r.address_components || []) {
            if (c.types.some((t) => types.includes(t)) && !isPlusCode(c.long_name)) {
              return c.long_name;
            }
          }
        }
        return undefined;
      };
      // Most-specific readable name first; region/state read better as "near X"
      // because a dropped pin isn't actually the town centre.
      const near = pick(['locality', 'postal_town', 'sublocality', 'natural_feature', 'park', 'tourist_attraction']);
      if (near) return near;
      const area = pick(['administrative_area_level_2', 'administrative_area_level_1']);
      if (area) return `Stop near ${area}`;
      const addr = results.find(
        (r) => r.formatted_address && !isPlusCode(r.formatted_address)
      )?.formatted_address;
      return addr || fallback;
    } catch {
      return fallback;
    }
  }, []);

  // ── After an add: show where the stop went ──────────────────────────────
  // The stop that just landed glows in the list (and its pin drops in once).
  const [highlightStopId, setHighlightStopId] = useState<string | null>(null);
  useEffect(() => {
    if (!highlightStopId) return;
    const t = window.setTimeout(() => setHighlightStopId(null), 2400);
    return () => window.clearTimeout(t);
  }, [highlightStopId]);
  // When a stop was last added: the map frames the whole trip once the new
  // route lands, so you can check the stop sits in the right order.
  const fitAfterAddRef = useRef(0);
  // Bring a stop that just landed into view inside its own scroller (the
  // planner body), clear of the footer.
  const revealStop = useCallback((stopId: string) => {
    window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(`[data-stop-id="${stopId}"]`);
      // Phone sheet at half: the list stays where it is (top of the
      // itinerary, Day 1 in view). The map above shows the new stop and the
      // confirmation names it; scrolling away from Day 1 lost the thread.
      if (row?.closest('[data-testid="planner-sheet"][data-snap="half"]')) return;
      if (row) revealInScroller(row);
    }, 90);
  }, []);
  const markAdded = useCallback(
    (stopId: string) => {
      setHighlightStopId(stopId);
      fitAfterAddRef.current = Date.now();
      setActiveTripId(null);
      setMapViewMode('trip');
    },
    [setMapViewMode]
  );

  // ── The message a pin drop leaves on the map ─────────────────────────────
  const [mapToast, setMapToastState] = useState<MapToastState | null>(null);
  const mapToastSeq = useRef(0);
  const setMapToast = useCallback((t: Omit<MapToastState, 'id'> | null, keepId = false) => {
    // keepId: update the message in place (no re-fade) when its answer lands.
    setMapToastState((prev) => (t ? { ...t, id: keepId && prev ? prev.id : ++mapToastSeq.current } : null));
  }, []);
  useEffect(() => {
    if (!mapToast) return;
    const t = window.setTimeout(() => setMapToastState(null), mapToastLifetime(mapToast));
    return () => window.clearTimeout(t);
  }, [mapToast]);
  // Where the pin being named is (a pulsing dot while the lookup runs).
  const [pinPending, setPinPending] = useState<{ x: number; y: number } | null>(null);

  // "Move to best spot": offered beside Undo when the stop went somewhere
  // that costs clearly more driving than another gap would (a "+" on a leg
  // is an exact spot; a pin or a search already takes the cheapest gap).
  const moveToBestAction = useCallback(
    (next: TripStop[], stopId: string, name: string) => {
      const spot = betterSpot(next, stopId);
      if (!spot) return undefined;
      return {
        label: 'Move to best spot',
        onClick: () => {
          const moved = normaliseTripStops(moveStopToGap(tripStopsRef.current, stopId, spot.index));
          setTripStops(moved);
          const idx = moved.findIndex((s) => s.id === stopId);
          setTripStatusMessage({
            message: `Moved ${name}`,
            detail: idx >= 0 ? `${whereItWent(moved, idx)} · about ${spot.savedKm.toLocaleString('en-AU')} km less` : undefined,
            tone: 'success',
          });
          revealStop(stopId);
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Put a named pin on the trip (a map click, or "Add <town> instead").
  const commitPin = useCallback(
    (lat: number, lng: number, name: string, at?: { x: number; y: number }) => {
      const stop = createTripStopFromName(name, 'via', { lat, lng, nights: 0 });
      const prev = tripStopsRef.current;
      const result = mergeTripStopIntoList(prev, stop);
      if (!result.added) {
        setMapToast({ message: `${name} is already in your trip`, tone: 'warning', at });
        return;
      }
      const next = normaliseTripStops(result.next);
      setTripStops(next);
      markAdded(stop.id);
      setAddWaypointMode(false);
      // One confirmation, not two: the new pin drops in on the map and the
      // status line (with Undo and what it costs) says the rest.
      setMapToast(null);
      const idx = next.findIndex((s) => s.id === stop.id);
      setTripStatusMessage({
        message: `Added ${name}`,
        detail: idx >= 0 ? whereItWent(next, idx) : undefined,
        tone: 'success',
        measureCost: true,
        secondaryAction: moveToBestAction(next, stop.id, name),
        action: { label: 'Undo', onClick: () => { setTripStops(prev); setTripStatusMessage(null); } },
      });
      revealStop(stop.id);
    },
    [setMapToast, markAdded, revealStop, moveToBestAction]
  );

  // Drop-a-pin: name the spot (a real town, or "Near <park>"), refuse the
  // sea, and end the mode after one successful drop. Like Google Maps, a pin
  // is one deliberate act, not a mode that keeps eating clicks. A sea pin
  // isn't a dead end: the toast offers the nearest town on the coast.
  // One pin at a time: a second click while the first is still being named
  // would add two stops for one intent.
  const pinDropBusyRef = useRef(false);
  const pinSeqRef = useRef(0);
  // Every listing sits on land: the map's cached all-sites list doubles as
  // the "nearest land" index for a refused sea pin. Loaded once, on demand.
  const landPointsRef = useRef<Promise<LandPoint[]> | null>(null);
  const loadLandPoints = useCallback(() => {
    if (!landPointsRef.current) {
      landPointsRef.current = fetchAllMapPoints().then((pts) =>
        pts
          .map((e) => ({
            lat: Number(e.addresses?.[0]?.gps_latitude),
            lng: Number(e.addresses?.[0]?.gps_longitude),
            name: e.name,
          }))
          .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
      );
      // A failed load can be retried on the next sea pin.
      landPointsRef.current.then((pts) => {
        if (!pts.length) landPointsRef.current = null;
      });
    }
    return landPointsRef.current;
  }, []);

  const handleAddWaypointAt = useCallback(
    async (lat: number, lng: number, at?: { x: number; y: number }) => {
      if (pinDropBusyRef.current) return;
      pinDropBusyRef.current = true;
      const seq = ++pinSeqRef.current;
      setMapToast(null);
      setPinPending(at ?? null);
      try {
        const where = await reverseGeocodePlace(lat, lng);
        if (where.status === 'water') {
          // One message, hanging off a pin where the click landed. Mode stays
          // on so they can pick again, or take the nearest town.
          const message = 'That spot’s in the water.';
          const retry = { label: 'Pick another spot', onClick: () => setMapToast(null) };
          setMapToast({ message, tone: 'warning', at, pending: 'Looking for the nearest town…', secondary: retry });
          pinDropBusyRef.current = false;
          setPinPending(null);
          const town = await nearestLand(lat, lng, loadLandPoints);
          if (seq !== pinSeqRef.current) return; // they've clicked again
          setMapToast(
            town
              ? {
                  message,
                  tone: 'warning',
                  at,
                  // Say what it is: the nearest TOWN (not the nearest
                  // coastline), how far, and which way.
                  detail: `Nearest town is ${town.name}, ${Math.max(1, Math.round(town.km)).toLocaleString('en-AU')} km ${compassWord({ lat, lng }, town)}.`,
                  action: {
                    label: `Add ${town.name} instead`,
                    onClick: () => commitPin(town.lat, town.lng, town.name),
                  },
                  secondary: retry,
                }
              : { message: 'That spot’s in the water. Pick somewhere on land.', tone: 'warning', at, secondary: retry },
            true
          );
          return;
        }
        // Dropped beside the route? Put it ON the route, so the reroute stays
        // on the highway instead of hairpinning out to the click and back.
        // "Beside" is what reads as on-the-line at this zoom: ~36 px.
        const zoom = mapRef?.current?.getZoom?.() ?? 9;
        const kmPerPx = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom / 1000;
        const snap = snapPinToRoute(routePathRef?.current ?? null, { lat, lng }, Math.min(10, Math.max(0.3, kmPerPx * 36)));
        let named = where;
        if (snap.snapped) {
          const onRoad = await reverseGeocodePlace(snap.lat, snap.lng);
          if (onRoad.status === 'place') named = onRoad;
        }
        // Lookup down: Google's geocoder can still name it. Never "-38.377...".
        const name =
          named.status === 'place'
            ? named.place.name
            : (await reverseGeocodeName(snap.lat, snap.lng)).replace(/^Custom stop .*$/, 'Dropped pin');
        commitPin(snap.lat, snap.lng, name, at);
      } finally {
        pinDropBusyRef.current = false;
        setPinPending(null);
      }
    },
    [reverseGeocodeName, setMapToast, commitPin, loadLandPoints, mapRef, routePathRef]
  );

  // Add a stop by typing a place name. Same destination as dropping a pin —
  // it goes through mergeTripStopIntoList so ordering, the locked start/end
  // guards and the "insert before the tail" behaviour are identical — but the
  // coordinates and the name come from /api/geocode rather than a map click,
  // so there is no reverse-geocode step and no dependency on Google.
  const handleAddPlaceStop = useCallback(
    (place: PickedPlace) => {
      // A town typed mid-trip is a pass-through until you say otherwise. It
      // used to start as a 1-night overnight, which quietly changed the
      // trip's night count; "Add a night" on the row adds one. A campsite is
      // somewhere you sleep, so it keeps its night. Start/destination get 0
      // nights automatically (normaliseTripStops).
      const stop = stopFromPlace(place);
      // Computed from the current list (not inside the state updater) so the
      // confirmation below can say exactly where the stop went.
      const prev = tripStopsRef.current;
      const result = mergeTripStopIntoList(prev, stop);
      if (!result.added) {
        setTripStatusMessage({ message: `“${place.label}” is already in your trip`, tone: 'warning' });
        return;
      }
      const next = normaliseTripStops(result.next);
      setTripStops(next);
      markAdded(stop.id);
      // Deliberately no page scroll: the planner is where the user is typing.
      const count = next.length;
      const at = result.index ?? count - 1;
      if (count <= 1) {
        // The add-stop field already asks "Where to next?"; saying it again
        // in a status line was noise.
        setTripStatusMessage(null);
      } else if (result.asNewEnd) {
        const oldEnd = count >= 3 ? next[count - 2] : null;
        setTripStatusMessage({
          message: oldEnd ? `Added ${place.label} as the new end` : `Heading to ${place.label}`,
          detail: oldEnd ? `${oldEnd.name} is now a stop on the way` : undefined,
          tone: 'success',
          action: oldEnd
            ? { label: 'Undo', onClick: () => { setTripStops(prev); setTripStatusMessage(null); } }
            : undefined,
        });
      } else if (isLoopTrip(prev)) {
        // "between Melbourne and Melbourne" reads like a bug on a round trip.
        setTripStatusMessage({ message: `Added ${place.label} to your round trip`, tone: 'success' });
      } else {
        setTripStatusMessage({
          message: `Added ${place.label}`,
          detail: whereItWent(next, at),
          tone: 'success',
          measureCost: true,
          action: {
            label: 'Make it the end',
            onClick: () => {
              setTripStops((cur) => makeStopTheDestination(cur, stop.id));
              setTripStatusMessage({ message: `${place.label} is now your destination`, tone: 'success' });
            },
          },
        });
      }
      if (count > 1) revealStop(stop.id);
    },
    [markAdded, revealStop]
  );

  // "+" between two stops: the stop goes exactly there (no detour guessing),
  // as a pass-through unless it's a campsite.
  const handleInsertPlaceStop = useCallback(
    (place: PickedPlace, index: number) => {
      const stop = stopFromPlace(place);
      const prev = tripStopsRef.current;
      const result = insertStopAt(prev, stop, index);
      if (!result.added) {
        setTripStatusMessage({ message: `“${place.label}” is already in your trip`, tone: 'warning' });
        return;
      }
      const next = normaliseTripStops(result.next);
      setTripStops(next);
      markAdded(stop.id);
      setTripStatusMessage({
        message: `Added ${place.label}`,
        detail: whereItWent(next, result.index),
        tone: 'success',
        measureCost: true,
        secondaryAction: moveToBestAction(next, stop.id, place.label),
        action: { label: 'Undo', onClick: () => { setTripStops(prev); setTripStatusMessage(null); } },
      });
      revealStop(stop.id);
    },
    [markAdded, revealStop, moveToBestAction]
  );

  const handleChangeNights = useCallback((stopId: string, nights: number) => {
    setTripStops((prev) => setStopNights(prev, stopId, nights));
    setActiveTripId(null);
  }, []);

  const handleTripReorder = useCallback((newStops: TripStop[]) => {
    setTripStops(newStops);
  }, []);

  const handleTripRemove = useCallback((stopId: string) => {
    const r = removeStopWithUndo(tripStops, stopId);
    if (!r) return;
    setTripStops((prev) => prev.filter((stop) => stop.id !== stopId));
    setTripStatusMessage({
      message: `Removed “${r.removed.name}”`,
      tone: 'warning',
      action: {
        label: 'Undo',
        onClick: () => {
          setTripStops(r.restore);
          setTripStatusMessage(null);
        },
      },
    });
  }, [tripStops]);

  const handleTripEditNote = useCallback((stopId: string, notes: string) => {
    setTripStops((prev) =>
      prev.map((stop) => (stop.id === stopId ? { ...stop, notes } : stop))
    );
  }, []);

  const handleTripEditName = useCallback((stopId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setTripStops((prev) =>
      prev.map((stop) => (stop.id === stopId ? { ...stop, name: trimmed } : stop))
    );
  }, []);

  return {
    tripStops,
    setTripStops,
    tripStopsRef,
    activeTripId,
    setActiveTripId,
    tripStatusMessage,
    setTripStatusMessage,
    addWaypointMode,
    setAddWaypointMode,
    reverseGeocodeName,
    handleAddWaypointAt,
    handleAddPlaceStop,
    handleInsertPlaceStop,
    highlightStopId,
    fitAfterAddRef,
    mapToast,
    setMapToast,
    pinPending,
    handleChangeNights,
    handleTripReorder,
    handleTripRemove,
    handleTripEditNote,
    handleTripEditName,
  };
}

// ── Legs + totals: one source of truth ─────────────────────────────────────
// Routing is signed-in only (GraphHopper bills per request). Guests plan
// stops + nights and save, with "≈" straight-line figures and a prompt to
// sign in for the real route, drive times and night planning.
export function useTripLegs({
  tripStops,
  canRoute,
  stopIdsKey,
  vehicleRoute,
  googleLegs,
  vehicleType,
  preferRoad = false,
}: {
  tripStops: TripStop[];
  canRoute: boolean;
  stopIdsKey: string;
  vehicleRoute: VehicleRouteState | null;
  googleLegs: GoogleLegsState | null;
  /** Car-route times stretch to the rig's pace: a caravan is slower. */
  vehicleType?: string;
  /** Road-route figures first for everyone (see pickTripLegs). */
  preferRoad?: boolean;
}) {
  // Road figures per pair of stops, so a reorder keeps every leg it has
  // already measured while the new order is routed (one answer per trip).
  const [roadLegCache, setRoadLegCache] = useState<RoadLegCache>({});
  useEffect(() => {
    const sets = [canRoute ? vehicleRoute : null, googleLegs];
    sets.forEach((set) => {
      if (!set || set.key !== stopIdsKey || set.legs.length !== tripStops.length - 1) return;
      setRoadLegCache((prev) => rememberRoadLegs(prev, tripStops, set.legs));
    });
  }, [vehicleRoute, googleLegs, stopIdsKey, tripStops, canRoute]);
  const tripLegInfo = useMemo((): { legs: LadderLeg[]; source: LegSource } => {
    const picked = pickTripLegs(tripStops, { signedIn: canRoute, stopIdsKey, vehicle: vehicleRoute, google: googleLegs, cache: roadLegCache, preferRoad });
    return { ...picked, legs: paceLegs(picked.legs, picked.source, vehicleType) };
  }, [tripStops, stopIdsKey, vehicleRoute, googleLegs, canRoute, roadLegCache, vehicleType, preferRoad]);
  const tripLegs = tripLegInfo.legs;
  // Header, accordion summary, print sheet: always the sum of the legs shown.
  const routeSummary = useMemo(() => totalsFromLegs(tripLegs), [tripLegs]);
  return { tripLegInfo, tripLegs, routeSummary };
}

// ── Plan my nights ─────────────────────────────────────────────────────────
// Day split + campsite picks need the routed drive times → signed in only.
export function useNightPlan({
  canRoute,
  tripStops,
  tripLegs,
  maxDailyDriveHours,
  tripStartDate,
  vehicleRoute,
  stopIdsKey,
  slotNames,
}: {
  canRoute: boolean;
  tripStops: TripStop[];
  tripLegs: LadderLeg[];
  maxDailyDriveHours: number;
  tripStartDate: string | null;
  vehicleRoute: VehicleRouteState | null;
  stopIdsKey: string;
  slotNames: Record<string, string>;
}) {
  const nightPlanBase = useMemo(() => {
    if (!canRoute || tripStops.length < 2) return null;
    const vr = vehicleRoute && vehicleRoute.key === stopIdsKey ? vehicleRoute : null;
    return planNights({
      stops: tripStops,
      legs: tripLegs,
      maxDriveMinutes: maxDailyDriveHours * 60,
      startDate: tripStartDate,
      pointAt: vr
        ? (legIndex, fraction) =>
            pointAlongPolyline(
              vr.polyline,
              vr.cumulativeKm,
              vr.vertexIndex[legIndex],
              vr.vertexIndex[legIndex + 1],
              fraction
            )
        : undefined,
    });
  }, [canRoute, tripStops, tripLegs, maxDailyDriveHours, tripStartDate, vehicleRoute, stopIdsKey]);
  const nightPlan = useMemo(
    () => (nightPlanBase ? withSlotNames(nightPlanBase, slotNames) : null),
    [nightPlanBase, slotNames]
  );
  return { nightPlanBase, nightPlan };
}

// ── Route figures: wait for the one that sticks ───────────────────────────
/**
 * True while a better route for `key` is still on its way (and `settled` is
 * false), for at most `timeoutMs`. The header shows "Working out the road…"
 * meanwhile, so the same trip never reads 798 km, then 1,019 km, then a new
 * drive time a second later. After the timeout it shows what it has.
 * An empty key means there is nothing to route.
 */
export function useRoutePending(key: string, settled: boolean, timeoutMs = 10000): boolean {
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    if (!key || settled) return;
    const t = setTimeout(() => setExpired(key), timeoutMs);
    return () => clearTimeout(t);
  }, [key, settled, timeoutMs]);
  return !!key && !settled && expired !== key;
}
